import React, { useRef, useState } from 'react';
import { Modal, View, Text, TouchableOpacity, Platform, ScrollView, TextInput, Switch } from 'react-native';
import Select from '../../../common/components/ui/Select';
import BasicModalContainer from '../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../common/components/basicmodal/ModalFooter';
import { DriverScreeningModalStyle } from './styles/DriverScreeningModalStyle';

type Props = {
  isModalVisible: boolean;
  closeModal: () => void;
  onSave?: (config: DrivingQuestionsConfig) => void;
  initialConfig?: DrivingQuestionsConfig;
};

export interface DriverLimitsConfig {
  maxTotal: number;
  maxAdditional: number;
}

export interface DriverSelectionOption {
  key: string;
  text: string;
  showsAdditionalDrivers: boolean;
}

export type DriverSelectionType = 'buttons' | 'radio' | 'dropdown';

export interface DriverSelectionConfig {
  type: DriverSelectionType;
  prompt: string;
  options: DriverSelectionOption[];
}

export interface QuestionCondition {
  field: string;
  value: any;
}

export interface AgreementQuestion {
  id: string;
  text: string;
  required: boolean;
  showWhen?: QuestionCondition;
}

export interface DrivingQuestionsConfig {
  driverLimits: DriverLimitsConfig;
  driverSelection?: DriverSelectionConfig; // optional per spec
  additionalDrivers: {
    prompt: string;
    addButtonText: string;
    maxReachedMessage: string;
  };
  questions: AgreementQuestion[];
}

const DEFAULT_CONFIG: DrivingQuestionsConfig = {
  driverLimits: {
    maxTotal: 4,
    maxAdditional: 3,
  },
  driverSelection: {
    type: 'buttons',
    prompt: 'Who will be driving? Maximum of {maxTotal} total drivers.',
    options: [
      {
        key: 'only_driver',
        text: 'I will be the only driver',
        showsAdditionalDrivers: false,
      },
      {
        key: 'additional_drivers',
        text: 'There are additional drivers',
        showsAdditionalDrivers: true,
      },
    ],
  },
  additionalDrivers: {
    prompt: 'Additional drivers (Maximum of {maxAdditional})',
    addButtonText: 'Add Driver',
    maxReachedMessage: 'Maximum of {maxAdditional} additional drivers allowed.',
  },
  questions: [
    {
      id: 'confirm_drivers',
      text: 'I understand that only named people will be driving the vehicle. I confirm that all drivers are 21+ with valid drivers license(s) and insurance.',
      required: true,
    },
    {
      id: 'speed_limit_agreement',
      text: 'I agree to drive 35MPH or less on roadways in the Moke or Honor LSV. Failure to do so will result in immediate termination of my rental without a refund.',
      required: true,
    },
    {
      id: 'damage_responsibility',
      text: 'I understand that there will be a $500 hold placed on my card upon arrival. I will be responsible for any and all damages.',
      required: true,
    },
    {
      id: 'return_charged',
      text: 'All overnight or multi-day rentals must be returned fully charged by 9am.',
      required: true,
    },
  ],
};

const EMPTY_CONFIG: DrivingQuestionsConfig = {
  driverLimits: {
    maxTotal: 1,
    maxAdditional: 0,
  },
  driverSelection: undefined,
  additionalDrivers: {
    prompt: '',
    addButtonText: '',
    maxReachedMessage: '',
  },
  questions: [],
};

const normalizeConfig = (incoming?: DrivingQuestionsConfig | null): DrivingQuestionsConfig => {
  if (!incoming || Array.isArray(incoming)) return EMPTY_CONFIG;

  return {
    driverLimits: incoming.driverLimits ?? EMPTY_CONFIG.driverLimits,
    driverSelection: incoming.driverSelection,
    additionalDrivers: incoming.additionalDrivers ?? EMPTY_CONFIG.additionalDrivers,
    questions: incoming.questions ?? EMPTY_CONFIG.questions,
  };
};

const DriverScreeningInfoModal = ({ isModalVisible, closeModal, onSave, initialConfig }: Props) => {
  if (Platform.OS !== 'web') return null;

  const [config, setConfig] = useState<DrivingQuestionsConfig>(normalizeConfig(initialConfig));
  const [maxAdditionalInput, setMaxAdditionalInput] = useState<string>(
    String(normalizeConfig(initialConfig).driverLimits.maxAdditional),
  );
  const idCounterRef = useRef<number>(DEFAULT_CONFIG.questions.length);
  const NON_REMOVABLE_IDS = useRef<Set<string>>(new Set(DEFAULT_CONFIG.questions.map(q => q.id)));
  const hasHydratedInitialConfig = useRef<boolean>(false);

  React.useEffect(() => {
    if (initialConfig === undefined || initialConfig === null || Array.isArray(initialConfig)) return;

    const normalized = normalizeConfig(initialConfig);
    const driverSelectionHasContent =
      !!normalized.driverSelection &&
      ((normalized.driverSelection.prompt?.trim?.() ?? '').length > 0 || normalized.driverSelection.options?.length > 0);
    const hasContent = driverSelectionHasContent || normalized.questions.length > 0;
    if (!hasContent) {
      if (hasHydratedInitialConfig.current) return;
      return;
    }

    setConfig(normalized);
    idCounterRef.current = normalized.questions.length;
    setMaxAdditionalInput(String(normalized.driverLimits.maxAdditional));
    hasHydratedInitialConfig.current = true;
  }, [initialConfig]);

  React.useEffect(() => {
    setMaxAdditionalInput(String(config.driverLimits.maxAdditional));
  }, [config.driverLimits.maxAdditional]);

  const resetToDefault = () => {
    setConfig(DEFAULT_CONFIG);
    idCounterRef.current = DEFAULT_CONFIG.questions.length;
    setMaxAdditionalInput(String(DEFAULT_CONFIG.driverLimits.maxAdditional));
    hasHydratedInitialConfig.current = true;
  };

  const handleAdditionalCountChange = (value: string) => {
    setMaxAdditionalInput(value);
    const digitsOnly = value.replace(/[^0-9]/g, '');
    if (digitsOnly === '') return;

    const numeric = Math.max(1, Number(digitsOnly));
    setConfig(prev => ({
      ...prev,
      driverLimits: {
        maxAdditional: numeric,
        maxTotal: numeric + 1, // primary driver + additional
      },
    }));
  };

  const handleAdditionalCountBlur = () => {
    const digitsOnly = maxAdditionalInput.replace(/[^0-9]/g, '');
    const numeric = Math.max(1, digitsOnly ? Number(digitsOnly) : 1);
    setMaxAdditionalInput(String(numeric));
    setConfig(prev => ({
      ...prev,
      driverLimits: {
        maxAdditional: numeric,
        maxTotal: numeric + 1,
      },
    }));
  };

  const ensureDriverSelection = (prev: DrivingQuestionsConfig): DriverSelectionConfig =>
    prev.driverSelection ?? { type: 'buttons', prompt: '', options: [] };

  const handleDriverSelectionChange = (field: 'type' | 'prompt', value: string) => {
    setConfig(prev => {
      const current = ensureDriverSelection(prev);
      return {
        ...prev,
        driverSelection: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const handleOptionChange = (index: number, field: keyof DriverSelectionOption, value: string | boolean) => {
    setConfig(prev => {
      const current = ensureDriverSelection(prev);
      return {
        ...prev,
        driverSelection: {
          ...current,
          options: current.options.map((option, idx) => (idx === index ? { ...option, [field]: value } : option)),
        },
      };
    });
  };

  const handleAdditionalDriversChange = (field: keyof DrivingQuestionsConfig['additionalDrivers'], value: string) => {
    setConfig(prev => ({
      ...prev,
      additionalDrivers: {
        ...prev.additionalDrivers,
        [field]: value,
      },
    }));
  };

  const handleQuestionChange = (index: number, field: keyof AgreementQuestion, value: string | boolean) => {
    setConfig(prev => ({
      ...prev,
      questions: prev.questions.map((question, idx) =>
        idx === index ? { ...question, [field]: value } : question,
      ),
    }));
  };

  const addQuestion = () => {
    const nextId = `question_${idCounterRef.current + 1}`;
    idCounterRef.current += 1;
    setConfig(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          id: nextId,
          text: '',
          required: false,
        },
      ],
    }));
  };

  const removeQuestion = (index: number) => {
    setConfig(prev => {
      const target = prev.questions[index];
      if (!target || NON_REMOVABLE_IDS.current.has(target.id)) return prev;
      return {
        ...prev,
        questions: prev.questions.filter((_, idx) => idx !== index),
      };
    });
  };

  const handleSave = () => {
    onSave?.(config);
    closeModal();
  };

  return (
    <Modal visible={isModalVisible} transparent animationType="fade" onRequestClose={closeModal}>
      <BasicModalContainer contentStyle={{ width: '90%', maxWidth: 960 }}>
        <ModalHeader label={'Questionnaire'} closeModal={closeModal} />
        <ModalBody>
          <ScrollView contentContainerStyle={DriverScreeningModalStyle.scrollContent}>
            {config.driverSelection && (
              <View style={DriverScreeningModalStyle.section}>
                {/* <Text style={DriverScreeningModalStyle.sectionTitle}>Driver Selection</Text> */}
                <View style={DriverScreeningModalStyle.rowGap}>
                  <View style={DriverScreeningModalStyle.inputGroup}>
                    <Text style={DriverScreeningModalStyle.label}>Question</Text>
                    <TextInput
                      value={config.driverSelection.prompt}
                      onChangeText={value => handleDriverSelectionChange('prompt', value)}
                      style={[DriverScreeningModalStyle.input, DriverScreeningModalStyle.multiline]}
                      multiline
                    />
                    {/* <Text style={DriverScreeningModalStyle.optionDescription}>
                      Use {'{maxTotal}'} to show the total drivers allowed (primary + additional).
                    </Text> */}
                  </View>
                  <View style={DriverScreeningModalStyle.inputGroup}>
                    <Text style={DriverScreeningModalStyle.label}>Max additional drivers</Text>
                    <TextInput
                      value={maxAdditionalInput}
                      keyboardType="numeric"
                      onChangeText={handleAdditionalCountChange}
                      onBlur={handleAdditionalCountBlur}
                      style={DriverScreeningModalStyle.input}
                    />
                    <Text style={DriverScreeningModalStyle.label}>
                      Total drivers allowed will be {config.driverLimits.maxAdditional + 1} (primary + additional).
                    </Text>
                  </View>
                </View>

                <View style={DriverScreeningModalStyle.inputGroup}>
                  <Select
                    label={'Type'}
                    options={[
                      { label: 'Buttons', value: 'buttons' },
                      { label: 'Radio', value: 'radio' },
                      { label: 'Dropdown', value: 'dropdown' },
                    ]}
                    value={config.driverSelection.type}
                    onChange={newValue => handleDriverSelectionChange('type', newValue as DriverSelectionType)}
                    placeholder="Select"
                  />
                </View>

                {config.driverSelection.options.map((option, index) => (
                  <View key={option.key} style={DriverScreeningModalStyle.card}>
                    <View style={DriverScreeningModalStyle.cardHeader}>
                      <Text style={DriverScreeningModalStyle.cardTitle}>Option {index + 1}</Text>
                    </View>
                    <View style={DriverScreeningModalStyle.inputGroup}>
                      {/* <Text style={DriverScreeningModalStyle.label}>Text</Text> */}
                      <TextInput
                        value={option.text}
                        onChangeText={value => handleOptionChange(index, 'text', value)}
                        style={DriverScreeningModalStyle.input}
                      />
                    </View>
                    {option.key === 'additional_drivers' && (
                      <Text style={DriverScreeningModalStyle.optionDescription}>
                        Selecting this will show additional driver inputs to the renter.
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            <View style={DriverScreeningModalStyle.section}>
              <Text style={DriverScreeningModalStyle.sectionTitle}>Questions</Text>
              {config.questions.map((question, index) => {
                const isProtected = NON_REMOVABLE_IDS.current.has(question.id);
                return (
                  <View key={question.id} style={DriverScreeningModalStyle.card}>
                    <View style={DriverScreeningModalStyle.cardHeader}>
                      <Text style={DriverScreeningModalStyle.cardTitle}>Question {index + 1}</Text>
                      <View style={DriverScreeningModalStyle.switchRow}>
                        <Text style={DriverScreeningModalStyle.switchLabel}>Required</Text>
                        <Switch
                          value={question.required}
                          onValueChange={value => handleQuestionChange(index, 'required', value)}
                        />
                        {!isProtected && (
                          <TouchableOpacity onPress={() => removeQuestion(index)} style={DriverScreeningModalStyle.removeButton}>
                            <Text style={DriverScreeningModalStyle.removeButtonText}>Remove</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>
                    <View style={DriverScreeningModalStyle.inputGroup}>
                      {/* <Text style={DriverScreeningModalStyle.label}>Text</Text> */}
                      <TextInput
                        value={question.text}
                        onChangeText={value => handleQuestionChange(index, 'text', value)}
                        style={[DriverScreeningModalStyle.input, DriverScreeningModalStyle.multiline]}
                        multiline
                      />
                    </View>
                  </View>
                );
              })}
              <TouchableOpacity onPress={addQuestion} style={DriverScreeningModalStyle.addButton}>
                <Text style={DriverScreeningModalStyle.addButtonText}>Add question</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </ModalBody>
        <ModalFooter>
          <View style={DriverScreeningModalStyle.footerActions}>
            <TouchableOpacity onPress={resetToDefault} style={DriverScreeningModalStyle.ghostButton}>
              <Text style={DriverScreeningModalStyle.ghostButtonText}>Reset to default</Text>
            </TouchableOpacity>
            <View style={DriverScreeningModalStyle.footerButtons}>
              <TouchableOpacity onPress={handleSave} style={DriverScreeningModalStyle.saveButton}>
                <Text style={DriverScreeningModalStyle.saveButtonText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ModalFooter>
      </BasicModalContainer>
    </Modal>
  );
};

export default DriverScreeningInfoModal;
